/**
 * masthead-content.ts — menyen i headeren på offentlige sider (norsk + engelsk)
 *
 * «Produkter» er en mega-meny (kategorier + elementer, som ibm.com); «Plattform» og «Kontakt»
 * er vanlige lenker. Alle lenker går til sider/ankre som finnes (forsidens
 * #plattform, #moduler og #feature-*). Offentlig side: statisk innhold i
 * frontend er tillatt. Forsidevariantene (/forside-2 … /forside-6) har ingen lenke i menyen. Typer: MastheadMenu i src/types/landing.ts.
 */

import type { Locale, MastheadMenu } from '@/types/landing';

const no: MastheadMenu = {
  entries: [
    {
      kind: 'mega',
      id: 'programvare',
      label: 'Programvare',
      categories: [
        {
          id: 'filservice',
          label: 'Filservice',
          heading: 'Filservice',
          intro: 'Send ECU-filer, følg jobben og last ned den modifiserte filen.',
          links: [
            { label: 'Slik fungerer det', href: '/infiles#steg' },
            { label: 'Logg inn', href: '/login' },
          ],
          items: [
            { label: 'inFiles', description: 'Filservice for chiptuning', href: '/infiles', status: 'Alpha', icon: 'transfer' },
          ],
        },
        {
          id: 'moduler',
          label: 'Forretningsmoduler',
          heading: 'Forretningsmoduler',
          intro: 'Utleie, annonsering, kunder og tilgang, samlet i én innlogging.',
          links: [{ label: 'Felles for hele plattformen', href: '/#funksjoner' }],
          items: [
            { label: 'inRental', description: 'Utleie med bookingkalender og bookingliste', href: '/#feature-inrental', icon: 'rental' },
            { label: 'inAds', description: 'Dashboard for Google Ads-annonsering', href: '/#feature-inads', icon: 'ads' },
            { label: 'CRM', description: 'Selskaper, personer og kunder på ett sted', href: '/#feature-crm', icon: 'crm' },
            { label: 'Administrasjon', description: 'Brukere, roller og tenants', href: '/#feature-admin', icon: 'admin' },
          ],
        },
      ],
      all: { label: 'Se alle programvaremoduler', href: '/#moduler' },
    },
    {
      kind: 'mega',
      id: 'infrastruktur',
      label: 'Infrastruktur',
      categories: [
        {
          id: 'hosting',
          label: 'Hosting og servere',
          heading: 'Hosting og servere',
          intro: 'Webhotell, WordPress-, Node.js- og GPU-hosting, eller en egen virtuell server når du vil styre alt selv.',
          links: [
            { label: 'Se pakker og priser', href: '/webhotell#pakker' },
            { label: 'Sammenlign VPS-størrelser', href: '/vps#sammenlign' },
          ],
          items: [
            { label: 'inHost', description: 'Webhotell med drift, sikkerhet og support', href: '/webhotell', icon: 'server' },
            { label: 'inWordPress', description: 'WordPress-hosting med automatiske oppdateringer og backup', href: '/wordpress', icon: 'wordpress' },
            { label: 'inNode', description: 'Node.js-hosting med deploy fra Git', href: '/nodejs', icon: 'nodejs' },
            { label: 'inGPU', description: 'GPU-deploy for AI og tung beregning', href: '/gpu', icon: 'chip' },
            { label: 'inVPS', description: 'Virtuelle servere med full rotilgang', href: '/vps', icon: 'virtual' },
          ],
        },
        {
          id: 'domener',
          label: 'Domener',
          heading: 'Domener',
          intro: 'Søk, registrer og flytt domenenavn, og sammenlign priser på alle endelser.',
          links: [
            { label: 'Se alle endelser', href: '/domener/endelser' },
            { label: 'Flytt domenet', href: '/domener/flytt' },
          ],
          items: [
            { label: 'Søk etter domener', description: 'Finn og sjekk tilgjengeligheten av domenenavn.', href: '/domener/sok', icon: 'search' },
            { label: 'Kjøp et domene', description: 'Registrer et domenenavn for nettstedet ditt.', href: '/domener/kjop', icon: 'buy' },
            { label: 'Overfør domene', description: 'Flytt ditt eksisterende domene til inSuite.', href: '/domener/flytt', icon: 'move' },
            { label: 'Fullstendig TLD-liste', description: 'Sammenlign priser på tvers av alle endelser.', href: '/domener/endelser', icon: 'list' },
          ],
        },
      ],
      all: { label: 'Utforsk hosting', href: '/webhotell' },
    },
    { kind: 'link', id: 'plattform', label: 'Plattform', href: '/#plattform' },
    { kind: 'link', id: 'kontakt', label: 'Kontakt', href: '/kontakt' },
  ],
  labels: {
    openMenu: 'Åpne meny',
    closeMenu: 'Lukk meny',
    back: 'Tilbake',
    mobileMenu: 'Hovedmeny',
    categories: 'Kategorier',
    products: 'Produkter',
    shortcuts: 'Snarveier',
  },
};

const en: MastheadMenu = {
  entries: [
    {
      kind: 'mega',
      id: 'programvare',
      label: 'Software',
      categories: [
        {
          id: 'filservice',
          label: 'File service',
          heading: 'File service',
          intro: 'Send ECU files, follow the job and download the modified file.',
          links: [
            { label: 'How it works', href: '/infiles#steg' },
            { label: 'Log in', href: '/login' },
          ],
          items: [
            { label: 'inFiles', description: 'A file service for chip tuning', href: '/infiles', status: 'Alpha', icon: 'transfer' },
          ],
        },
        {
          id: 'moduler',
          label: 'Business modules',
          heading: 'Business modules',
          intro: 'Rentals, advertising, customers and access, under one login.',
          links: [{ label: 'Shared across the platform', href: '/#funksjoner' }],
          items: [
            { label: 'inRental', description: 'Rentals with a booking calendar and a booking list', href: '/#feature-inrental', icon: 'rental' },
            { label: 'inAds', description: 'Dashboard for Google Ads advertising', href: '/#feature-inads', icon: 'ads' },
            { label: 'CRM', description: 'Companies, persons and customers in one place', href: '/#feature-crm', icon: 'crm' },
            { label: 'Administration', description: 'Users, roles and tenants', href: '/#feature-admin', icon: 'admin' },
          ],
        },
      ],
      all: { label: 'See all software modules', href: '/#moduler' },
    },
    {
      kind: 'mega',
      id: 'infrastruktur',
      label: 'Infrastructure',
      categories: [
        {
          id: 'hosting',
          label: 'Hosting and servers',
          heading: 'Hosting and servers',
          intro: 'Web, WordPress, Node.js and GPU hosting, or a virtual server of your own when you want full control.',
          links: [
            { label: 'See plans and pricing', href: '/webhotell#pakker' },
            { label: 'Compare VPS sizes', href: '/vps#sammenlign' },
          ],
          items: [
            { label: 'inHost', description: 'Web hosting with operations, security and support', href: '/webhotell', icon: 'server' },
            { label: 'inWordPress', description: 'WordPress hosting with automatic updates and backup', href: '/wordpress', icon: 'wordpress' },
            { label: 'inNode', description: 'Node.js hosting with deploy from Git', href: '/nodejs', icon: 'nodejs' },
            { label: 'inGPU', description: 'GPU deploy for AI and heavy computation', href: '/gpu', icon: 'chip' },
            { label: 'inVPS', description: 'Virtual servers with full root access', href: '/vps', icon: 'virtual' },
          ],
        },
        {
          id: 'domener',
          label: 'Domains',
          heading: 'Domains',
          intro: 'Search, register and transfer domain names, and compare prices across all extensions.',
          links: [
            { label: 'See all extensions', href: '/domener/endelser' },
            { label: 'Transfer a domain', href: '/domener/flytt' },
          ],
          items: [
            { label: 'Search for domains', description: 'Find and check the availability of domain names.', href: '/domener/sok', icon: 'search' },
            { label: 'Buy a domain', description: 'Register a domain name for your website.', href: '/domener/kjop', icon: 'buy' },
            { label: 'Transfer a domain', description: 'Move your existing domain to inSuite.', href: '/domener/flytt', icon: 'move' },
            { label: 'Full TLD list', description: 'Compare prices across all extensions.', href: '/domener/endelser', icon: 'list' },
          ],
        },
      ],
      all: { label: 'Explore hosting', href: '/webhotell' },
    },
    { kind: 'link', id: 'plattform', label: 'Platform', href: '/#plattform' },
    { kind: 'link', id: 'kontakt', label: 'Contact', href: '/kontakt' },
  ],
  labels: {
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    back: 'Back',
    mobileMenu: 'Main menu',
    categories: 'Categories',
    products: 'Products',
    shortcuts: 'Shortcuts',
  },
};

export const MASTHEAD_CONTENT: Record<Locale, MastheadMenu> = { no, en };
